// ── Welle D3b: Heute-Board am Handy (unter 1024 px) ──
// Zwei Spalten: „gross“ volle Breite, „klein“ halbe; ein kleines Widget ohne kleinen Nachbarn
// nimmt die volle Breite. Anpassen = Liste (Ziehen am Griff mit ~200 ms Touch-Verzögerung,
// angetippte Zeile bekommt Pfeile), Klein/Groß, Ausblenden; eigenes Layout „mobil“.
import React, { useCallback, useEffect, useState } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { MIcon } from '../../../lib/micon';
import { toast } from '../../../lib/toast';
import { confirm } from '../../../lib/confirm';
import { Button } from '../../../components/ld/Button';
import { WidgetHuelle } from './WidgetHuelle';
import { WidgetSheetHandy } from './WidgetMenu';
import { WIDGET_META, hatMeta } from './widget-meta';
import type { Groesse, MobilItem, MobilLayout, WidgetDef } from './layout-logik';
import { handyZeilen } from './layout-logik';
import type { WidgetInhalt } from './WidgetInhalt';

type Props = {
  layout: MobilLayout;
  defs: Map<string, WidgetDef>;
  inhalte: Record<string, WidgetInhalt>;
  bearbeiten: boolean;
  setBearbeiten: (b: boolean) => void;
  speichere: (l: MobilLayout, sofort?: boolean) => void;
  zuruecksetzen: () => Promise<void>;
};

export function BoardHandy({ layout, defs, inhalte, bearbeiten, setBearbeiten, speichere, zuruecksetzen }: Props) {
  const sichtbare = layout.widgets.filter(w => hatMeta(w.id) && defs.has(w.id));
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  /** Speichern; nicht angezeigte (unbekannte) IDs bleiben unter „ausgeblendet“ erhalten */
  const uebernehmen = useCallback((widgets: MobilItem[], ausgeblendet: string[], sofort = false) => {
    const drin = new Set(widgets.map(w => w.id));
    const unbekannt = layout.widgets.filter(w => !(hatMeta(w.id) && defs.has(w.id))).map(w => w.id);
    speichere({
      widgets,
      ausgeblendet: [...ausgeblendet.filter(id => !drin.has(id)), ...unbekannt.filter(id => !ausgeblendet.includes(id))],
    }, sofort);
  }, [layout.widgets, defs, speichere]);

  function verschiebe(id: string, richtung: -1 | 1) {
    const i = sichtbare.findIndex(w => w.id === id);
    const j = i + richtung;
    if (i < 0 || j < 0 || j >= sichtbare.length) return;
    uebernehmen(arrayMove(sichtbare, i, j), layout.ausgeblendet);
  }
  function setzeGroesse(id: string, g: Groesse) {
    if (!defs.get(id)?.handy.erlaubt.includes(g)) return;
    uebernehmen(sichtbare.map(w => w.id === id ? { ...w, groesse: g } : w), layout.ausgeblendet, true);
  }
  function ausblenden(id: string) {
    const vorher = layout;
    uebernehmen(sichtbare.filter(w => w.id !== id), [id, ...layout.ausgeblendet], true);
    toast(`${WIDGET_META[id]?.titel ?? 'Widget'} ausgeblendet`, 'info', {
      aktion: { label: 'Rückgängig', onClick: () => speichere(vorher, true) },
      dauerMs: 5000,
    });
  }
  function hinzufuegen(id: string) {
    const def = defs.get(id);
    if (!def) return;
    uebernehmen([...sichtbare, { id, groesse: def.handy.standard }], layout.ausgeblendet.filter(a => a !== id), true);
  }
  async function standardWiederherstellen() {
    const ok = await confirm('Standard-Anordnung am Handy wiederherstellen? Deine Anordnung geht verloren.', {
      title: 'Zurücksetzen', confirmLabel: 'Zurücksetzen', danger: true,
    });
    if (!ok) return;
    try { await zuruecksetzen(); toast('Standard-Anordnung wiederhergestellt'); }
    catch { toast('Zurücksetzen fehlgeschlagen. Bitte noch einmal versuchen.', 'error'); }
  }

  const sensoren = useSensors(
    // Maus und Touch getrennt: ein PointerSensor würde auch Touch-Zeiger sofort nach 6 px
    // übernehmen — dann griffe die Halte-Verzögerung (~200 ms) am Handy nie
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  function onDragEnd(e: DragEndEvent) {
    const von = sichtbare.findIndex(w => w.id === e.active.id);
    const nach = e.over ? sichtbare.findIndex(w => w.id === e.over!.id) : -1;
    if (von < 0 || nach < 0 || von === nach) return;
    uebernehmen(arrayMove(sichtbare, von, nach), layout.ausgeblendet);
  }

  useEffect(() => {
    if (!bearbeiten) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setBearbeiten(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [bearbeiten, setBearbeiten]);
  useEffect(() => { if (!bearbeiten) setGewaehlt(null); }, [bearbeiten]);

  const sheetItem = sheetId ? sichtbare.find(w => w.id === sheetId) : null;
  const sheetIndex = sheetItem ? sichtbare.indexOf(sheetItem) : -1;
  const ablage = layout.ausgeblendet.filter(id => hatMeta(id) && defs.has(id));

  if (bearbeiten) {
    return (
      <div className="ld-hboard-edit">
        <div className="ld-hboard-edit__kopf">
          <div>
            <h2>Board anpassen</h2>
            <p>Gilt nur am Handy. Am Griff halten und ziehen oder Zeile antippen und Pfeile nutzen.</p>
          </div>
          <Button variant="primary" onClick={() => setBearbeiten(false)}>Fertig</Button>
        </div>

        <DndContext sensors={sensoren} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={sichtbare.map(w => w.id)} strategy={verticalListSortingStrategy}>
            <ul className="ld-hboard-liste" aria-label="Widgets auf dem Board">
              {sichtbare.map((w, i) => (
                <SortierZeile
                  key={w.id}
                  item={w}
                  def={defs.get(w.id)!}
                  gewaehlt={gewaehlt === w.id}
                  istErstes={i === 0}
                  istLetztes={i === sichtbare.length - 1}
                  onWaehlen={() => setGewaehlt(g => g === w.id ? null : w.id)}
                  onHoch={() => verschiebe(w.id, -1)}
                  onRunter={() => verschiebe(w.id, 1)}
                  onGroesse={g => setzeGroesse(w.id, g)}
                  onAusblenden={() => ausblenden(w.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        {sichtbare.length === 0 && <p className="ld-ablage__leer">Dein Board ist leer. Hol dir unten Widgets dazu.</p>}

        <section className="ld-hboard-aus" aria-labelledby="ld-hboard-aus-titel">
          <h3 id="ld-hboard-aus-titel">Ausgeblendet</h3>
          {ablage.length === 0 ? (
            <p className="ld-ablage__leer">Alle Widgets sind auf dem Board.</p>
          ) : (
            <ul className="ld-hboard-liste">
              {ablage.map(id => (
                <li key={id} className="ld-hboard-zeile ld-hboard-zeile--aus">
                  <span className="ld-ablage__icon"><MIcon name={WIDGET_META[id].icon} size={18} /></span>
                  <span className="ld-hboard-zeile__name">{WIDGET_META[id].titel}<small>{WIDGET_META[id].beschreibung}</small></span>
                  <button type="button" className="ld-ablage__plus" aria-label={`${WIDGET_META[id].titel} hinzufügen`} onClick={() => hinzufuegen(id)}>
                    <MIcon name="add" size={20} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <button type="button" className="ld-hboard-reset" onClick={standardWiederherstellen}>
          <MIcon name="restart_alt" size={18} /> Standard-Anordnung wiederherstellen
        </button>
      </div>
    );
  }

  return (
    <>
      {sichtbare.length === 0 ? (
        <div className="ld-board__leer">
          <MIcon name="dashboard_customize" size={30} />
          <p>Dein Board ist leer. Hol dir Widgets aus der Ablage.</p>
          <Button variant="primary" onClick={() => setBearbeiten(true)}>Board anpassen</Button>
        </div>
      ) : (
        <div className="ld-hboard">
          {handyZeilen(sichtbare).map(({ item, voll }) => {
            const inhalt = inhalte[item.id];
            return (
              <div key={item.id} className={`ld-hboard__zelle${voll ? ' ld-hboard__zelle--voll' : ''}`}>
                <WidgetHuelle
                  id={item.id}
                  meta={WIDGET_META[item.id]}
                  kompakt={item.groesse === 'klein'}
                  groesse={item.groesse === 'klein' ? { w: 1, h: 1, geraet: 'handy' } : { w: 2, h: 2, geraet: 'handy' }}
                  zusatz={inhalt?.zusatz}
                  leerText={inhalt?.leerText}
                  onMenu={() => setSheetId(item.id)}
                >
                  {inhalt?.body ?? null}
                </WidgetHuelle>
              </div>
            );
          })}
        </div>
      )}
      <WidgetSheetHandy
        offen={!!sheetItem}
        titel={sheetItem ? WIDGET_META[sheetItem.id].titel : ''}
        def={sheetItem ? defs.get(sheetItem.id) ?? null : null}
        groesse={sheetItem?.groesse ?? 'gross'}
        istErstes={sheetIndex === 0}
        istLetztes={sheetIndex === sichtbare.length - 1}
        onGroesse={g => sheetItem && setzeGroesse(sheetItem.id, g)}
        onHoch={() => sheetItem && verschiebe(sheetItem.id, -1)}
        onRunter={() => sheetItem && verschiebe(sheetItem.id, 1)}
        onAusblenden={() => { const id = sheetItem?.id; setSheetId(null); if (id) window.setTimeout(() => ausblenden(id), 0); }}
        onAnpassen={() => { setSheetId(null); setBearbeiten(true); }}
        onClose={() => setSheetId(null)}
      />
    </>
  );
}

type ZeileProps = {
  item: MobilItem; def: WidgetDef; gewaehlt: boolean; istErstes: boolean; istLetztes: boolean;
  onWaehlen: () => void; onHoch: () => void; onRunter: () => void;
  onGroesse: (g: Groesse) => void; onAusblenden: () => void;
};

function SortierZeile({ item, def, gewaehlt, istErstes, istLetztes, onWaehlen, onHoch, onRunter, onGroesse, onAusblenden }: ZeileProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const meta = WIDGET_META[item.id];
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
  };
  return (
    <li ref={setNodeRef} style={style}
      className={`ld-hboard-zeile${gewaehlt ? ' ist-gewaehlt' : ''}${isDragging ? ' ist-gezogen' : ''}`}>
      <button type="button" ref={setActivatorNodeRef} className="ld-hboard-zeile__griff"
        aria-label={`${meta.titel} verschieben`} {...attributes} {...listeners}>
        <MIcon name="drag_indicator" size={20} />
      </button>
      <button type="button" className="ld-hboard-zeile__name" onClick={onWaehlen} aria-expanded={gewaehlt}>
        <MIcon name={meta.icon} size={16} className="ld-hboard-zeile__icon" />
        {meta.titel}
      </button>
      {gewaehlt && (
        <span className="ld-hboard-zeile__pfeile">
          <button type="button" aria-label={`${meta.titel} nach oben`} disabled={istErstes} onClick={onHoch}><MIcon name="arrow_upward" size={18} /></button>
          <button type="button" aria-label={`${meta.titel} nach unten`} disabled={istLetztes} onClick={onRunter}><MIcon name="arrow_downward" size={18} /></button>
        </span>
      )}
      <span className="ld-hboard-zeile__groesse" role="radiogroup" aria-label={`Größe ${meta.titel}`}>
        {(['klein', 'gross'] as Groesse[]).map(g => (
          <button key={g} type="button" role="radio" aria-checked={item.groesse === g}
            disabled={!def.handy.erlaubt.includes(g)}
            className={item.groesse === g ? 'ist-an' : ''}
            onClick={() => onGroesse(g)}>{g === 'klein' ? 'Klein' : 'Groß'}</button>
        ))}
      </span>
      <button type="button" className="ld-hboard-zeile__weg" aria-label={`${meta.titel} ausblenden`} onClick={onAusblenden}>
        <MIcon name="visibility_off" size={18} />
      </button>
    </li>
  );
}
